// src/components/NewsletterForm.js

import React, { useState } from 'react';
import styled from 'styled-components';
import { FiArrowRight, FiCheck, FiAlertCircle } from 'react-icons/fi';

// API URL - official XADevs API domain
const API_URL = 'https://api.xadevs.com';

// Estilos - Manteniendo la apariencia consistente con el resto del sitio
const FormContainer = styled.div`
  position: relative;
  width: 100%;
`;

const FormWrapper = styled.form`
  display: flex;
`;

const EmailInput = styled.input`
  flex: 1; 
  padding: 10px 12px;
  border: none;
  border-radius: 4px 0 0 4px;
  font-size: 0.9rem;
  
  &:focus {
    outline: none;
  }
`;

const SubmitButton = styled.button`
  background: var(--accent);
  border: none;
  color: white;
  padding: 0 15px;
  border-radius: 0 4px 4px 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color var(--transition-fast);
  
  &:hover {
    background-color: #3A9FE0;
  }
  
  &:disabled {
    background-color: #9FC4DA;
    cursor: not-allowed;
  }
`;

const MessageContainer = styled.div`
  margin-top: 8px;
  padding: 6px 10px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  font-size: 0.85rem;
  color: ${props => props.isError ? 'rgba(255, 66, 66, 1)' : 'rgba(255, 255, 255, 0.9)'};
  background-color: ${props => props.isError ? 'rgba(255, 66, 66, 0.1)' : 'rgba(76, 175, 80, 0.1)'};
  
  svg {
    margin-right: 6px;
    flex-shrink: 0;
  }
`;

// Componente principal
const NewsletterForm = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState(null);
  // Country lookup runs only when someone subscribes, never on page load:
  // no third-party request (or visitor IP shared) for people who just browse.
  const lookupCountry = async () => {
    try {
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 2500);
      const response = await fetch('https://ipapi.co/json/', { signal: controller.signal });
      clearTimeout(timeout);
      const data = await response.json();
      return (data && data.country_code) || '';
    } catch (error) {
      return '';
    }
  };
  
  // Obtener información del dispositivo y navegador
  const getBrowserInfo = () => {
    const userAgent = navigator.userAgent;
    let browser = 'Unknown';
    let os = 'Unknown';
    
    // Detectar navegador
    if (userAgent.indexOf('Chrome') > -1) browser = 'Chrome';
    else if (userAgent.indexOf('Safari') > -1) browser = 'Safari';
    else if (userAgent.indexOf('Firefox') > -1) browser = 'Firefox';
    else if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident/') > -1) browser = 'IE';
    else if (userAgent.indexOf('Edge') > -1) browser = 'Edge';
    
    // Detectar sistema operativo
    if (userAgent.indexOf('Windows') > -1) os = 'Windows';
    else if (userAgent.indexOf('Mac') > -1) os = 'MacOS';
    else if (userAgent.indexOf('Linux') > -1) os = 'Linux';
    else if (userAgent.indexOf('Android') > -1) os = 'Android';
    else if (userAgent.indexOf('iOS') > -1 || userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) os = 'iOS';
    
    // Detectar tipo de dispositivo
    let device = 'Desktop';
    if (/(tablet|ipad|playbook|silk)|(android(?!.*mobi))/i.test(userAgent)) {
      device = 'Tablet';
    } else if (/Mobile|iPhone|iPod|Android|webOS|BlackBerry|IEMobile|Opera Mini/i.test(userAgent)) {
      device = 'Mobile';
    }
    
    return { browser, os, device };
  };
  
  // Manejar envío del formulario
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validación básica
    if (!email || !email.includes('@') || !email.includes('.')) {
      setMessage({
        isError: true,
        text: 'Please enter a valid email address'
      });
      return;
    }
    
    setIsSubmitting(true);
    setMessage(null);
    
    try {
      // Obtener información del dispositivo y otros datos
      const { browser, os, device } = getBrowserInfo();
      const language = navigator.language || 'en';
      
      // Obtener UTM params si existen
      const urlParams = new URLSearchParams(window.location.search);
      const utmSource = urlParams.get('utm_source') || '';
      const utmMedium = urlParams.get('utm_medium') || '';
      const utmCampaign = urlParams.get('utm_campaign') || '';
      const utmContent = urlParams.get('utm_content') || '';
      const utmTerm = urlParams.get('utm_term') || '';
      
      // Metadata adicional
      const metadata = JSON.stringify({
        screen_width: window.innerWidth,
        screen_height: window.innerHeight,
        timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        subscribed_from: 'website_footer'
      });
      
      const countryCode = await lookupCountry();

      // Construir payload
      const payload = {
        email,
        device,
        browser,
        os,
        language,
        country: countryCode, // Agregando el código de país
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign,
        utm_content: utmContent,
        utm_term: utmTerm,
        referrer: document.referrer || '',
        metadata
      };
      
      console.log('Sending subscription data:', payload); // Para debugging
      
      // Enviar solicitud al servidor
      const response = await fetch(`${API_URL}/api/newsletter/subscribe`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setMessage({
          isError: false,
          text: data.message || 'Thanks for subscribing to our newsletter!'
        });
        setEmail('');
      } else {
        throw new Error(data.message || 'Failed to subscribe. Please try again.');
      }
    } catch (error) {
      console.error('Newsletter subscription error:', error);
      setMessage({
        isError: true,
        text: error.message || 'Something went wrong. Please try again later.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <FormContainer>
      <FormWrapper onSubmit={handleSubmit}>
        <EmailInput
          type="email"
          placeholder="Your email address"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          disabled={isSubmitting}
          aria-label="Email address"
        />
        <SubmitButton
          type="submit"
          disabled={isSubmitting}
          aria-label="Subscribe"
        >
          {isSubmitting ? '...' : <FiArrowRight />}
        </SubmitButton>
      </FormWrapper>
      
      {message && (
        <MessageContainer isError={message.isError}>
          {message.isError ? <FiAlertCircle /> : <FiCheck />}
          {message.text}
        </MessageContainer>
      )}
    </FormContainer>
  );
};

export default NewsletterForm;
